
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { generateProfileOverviewApi } from "./profile/profile-api";
import ProfileDetailsInput from "./profile/ProfileDetailsInput";
import OverviewDisplay from "./profile/OverviewDisplay";
import ErrorDisplay from "./profile/ErrorDisplay";
import { FileText, Eye, Wand2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const ProfileGenerator = () => {
  const [skills, setSkills] = useState("");
  const [experience, setExperience] = useState("");
  const [title, setTitle] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [profileOverview, setProfileOverview] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isQuotaExceeded, setIsQuotaExceeded] = useState(false);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [activeTab, setActiveTab] = useState("input");
  const { toast } = useToast();

  const handleGenerate = async () => {
    if (!skills.trim() || !experience.trim()) {
      setError("Please provide your skills and experience");
      return;
    }

    setIsGenerating(true);
    setError(null);
    setIsQuotaExceeded(false);
    setIsRateLimited(false);
    setCooldownRemaining(0);

    try {
      toast({
        title: "Generating profile",
        description: "Creating your professional profile overview...",
        duration: 3000,
      });
      
      const result = await generateProfileOverviewApi(
        skills,
        experience,
        title,
        targetAudience
      );

      if (result.error) {
        setError(result.error);
        setIsQuotaExceeded(result.isQuotaExceeded);
        setIsRateLimited(result.isRateLimited);
        
        toast({
          title: "Generation failed",
          description: "There was an error creating your profile. Please try again.",
          variant: "destructive",
        });
      } else if (result.overview) {
        setProfileOverview(result.overview);
        setActiveTab("result");
        
        toast({
          title: "Profile created!",
          description: "Your professional profile overview has been generated.",
        });
      }
    } catch (err: any) {
      setError(err.message || "Failed to generate profile. Please try again.");
      
      toast({
        title: "Generation failed",
        description: "There was an error creating your profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const resetForm = () => {
    setSkills("");
    setExperience("");
    setTitle("");
    setTargetAudience("");
    setProfileOverview("");
    setError(null);
    setIsQuotaExceeded(false);
    setIsRateLimited(false);
    setCooldownRemaining(0);
    setActiveTab("input");
  };

  return (
    <Card className="shadow-lg border border-muted/30 overflow-hidden">
      <CardContent className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex justify-center mb-8">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="input" className="data-[state=active]:bg-primary/10">
                <FileText className="h-4 w-4 mr-2" />
                <span>Input Details</span>
              </TabsTrigger>
              <TabsTrigger value="result" disabled={!profileOverview.length} className="data-[state=active]:bg-primary/10">
                <Eye className="h-4 w-4 mr-2" />
                <span>Generated Profile</span>
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="input" className="mt-0 px-1">
            <div className="bg-muted/5 rounded-xl p-5 border border-muted/20 shadow-sm">
              <div className="mb-6">
                <h2 className="text-lg font-medium">Create Your Professional Profile Overview</h2>
                <p className="text-muted-foreground text-sm">
                  Enter your details below to generate a professionally formatted profile for freelance platforms like Upwork and Fiverr.
                </p>
              </div>
              
              <ProfileDetailsInput
                skills={skills}
                setSkills={setSkills}
                experience={experience}
                setExperience={setExperience}
                title={title}
                setTitle={setTitle}
                targetAudience={targetAudience}
                setTargetAudience={setTargetAudience}
              />

              <ErrorDisplay
                error={error}
                isQuotaExceeded={isQuotaExceeded}
                isRateLimited={isRateLimited}
                cooldownRemaining={cooldownRemaining}
              />

              <div className="flex justify-end mt-8">
                <Button
                  onClick={handleGenerate}
                  disabled={
                    isGenerating ||
                    !skills.trim() ||
                    !experience.trim() ||
                    isQuotaExceeded ||
                    isRateLimited
                  }
                  className="px-6 py-2.5 text-base font-medium"
                  size="lg"
                >
                  {isGenerating ? (
                    <>
                      <span className="mr-2">Generating...</span>
                      <div className="h-4 w-4 rounded-full border-2 border-t-transparent animate-spin"></div>
                    </>
                  ) : (
                    <>
                      <Wand2 className="mr-2 h-5 w-5" />
                      Generate Overview
                    </>
                  )}
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="result" className="mt-0 px-1">
            <div className="bg-muted/5 rounded-xl p-5 border border-muted/20 shadow-sm">
              <div className="mb-6">
                <h2 className="text-lg font-medium">Your Professional Profile Overview</h2>
                <p className="text-muted-foreground text-sm">
                  Here's your generated profile overview. Feel free to copy it and make any necessary adjustments.
                </p>
              </div>
              
              <OverviewDisplay
                profileOverview={profileOverview}
                onReset={resetForm}
              />
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default ProfileGenerator;
